import { useEffect, useState } from "react";
import { useIsVerified } from "src/components/email-verification";
import { Alert, AlertDescription } from "src/components/ui/alert";
import { Button } from "src/components/ui/button";
import { Card, CardContent, CardHeader } from "src/components/ui/card";
import { Input } from "src/components/ui/input";
import { Label } from "src/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "src/components/ui/select";
import { Slider } from "src/components/ui/slider";
import {
  checkCalculatorUsageLimitAndRedirect,
  loadSavedCalculatorData,
  saveCalculatorData,
} from "src/utils/functions";
import { sanitizeBrlValue } from "../simulador-poupanca-selic";
import { EmergencyFundResponse } from "./components/EmergencyFundResponse";

interface EmergencyFundResults {
  employmentType: string;
  monthsNeeded: number;
  emergencyFundAmount: number;
  savingsPercentage: number;
  monthsToComplete: number;
}

const calculatorKey = "reserva-emergencia";

export const ReservaEmergencia = () => {
  const [employmentType, setEmploymentType] = useState<string>("");
  const [fixedCosts, setFixedCosts] = useState<string>("");
  const [monthlySalary, setMonthlySalary] = useState<string>("");
  const [savingsPercentage, setSavingsPercentage] = useState<number>(10);
  const [error, setError] = useState<string>("");
  const [results, setResults] = useState<EmergencyFundResults | null>(null);
  const isVerified = useIsVerified();

  function clear() {
    setEmploymentType("");
    setFixedCosts("");
    setMonthlySalary("");
    setSavingsPercentage(10);
    setResults(null);
    setError("");
  }

  const calculateEmergencyFund = () => {
    setError("");

    if (!employmentType || !fixedCosts || !monthlySalary) {
      setError("Por favor, preencha todos os campos");
      return;
    }

    const fixedCostsAmount = Number.parseFloat(
      fixedCosts.replace(/[^0-9,-]+/g, "").replace(",", ".")
    );
    const monthlySalaryAmount = Number.parseFloat(
      monthlySalary.replace(/[^0-9,-]+/g, "").replace(",", ".")
    );

    if (fixedCostsAmount <= 0 || monthlySalaryAmount <= 0) {
      setError("Todos os valores devem ser maiores que zero");
      return;
    }

    let monthsNeeded: number;
    switch (employmentType) {
      case "funcionário público":
        monthsNeeded = 3;
        break;
      case "clt":
        monthsNeeded = 6;
        break;
      case "mei/autônomo/empreendedor":
        monthsNeeded = 12;
        break;
      default:
        setError("Tipo de emprego inválido");
        return;
    }

    const emergencyFundAmount = fixedCostsAmount * monthsNeeded;
    const monthlySavings = (monthlySalaryAmount * savingsPercentage) / 100;
    const monthsToComplete = Math.ceil(emergencyFundAmount / monthlySavings);

    setResults({
      employmentType,
      monthsNeeded,
      emergencyFundAmount,
      savingsPercentage,
      monthsToComplete,
    });
  };

  function handleSave() {
    saveCalculatorData(calculatorKey, {
      employmentType,
      fixedCosts,
      monthlySalary,
      savingsPercentage,
    });
  }

  useEffect(() => {
    const savedData = loadSavedCalculatorData(calculatorKey);
    if (!savedData) return;
    setEmploymentType((savedData.employmentType as string) || "");
    setFixedCosts((savedData.fixedCosts as string) || "");
    setMonthlySalary((savedData.monthlySalary as string) || "");
    setSavingsPercentage((savedData.savingsPercentage as number) || 10);
  }, []);

  return (
    <div className="w-full max-w-4xl mx-auto p-4">
      <Card>
        <CardHeader>
          <h1 className="text-primary text-xl font-bold">
            Calculadora Reserva de Emergência
          </h1>
        </CardHeader>
        <CardContent>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleSave();
              checkCalculatorUsageLimitAndRedirect(calculateEmergencyFund);
            }}
            className="grid gap-4"
          >
            <div className="grid gap-2">
              <Label htmlFor="employmentType">Tipo de Emprego</Label>
              <Select value={employmentType} onValueChange={setEmploymentType}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Selecione o tipo de emprego" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="funcionário público">
                    Funcionário Público
                  </SelectItem>
                  <SelectItem value="clt">CLT</SelectItem>
                  <SelectItem value="mei/autônomo/empreendedor">
                    MEI/Autônomo/Empreendedor
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="grid gap-2">
              <Label htmlFor="fixedCosts">Custo Fixo</Label>
              <div className="relative">
                <Input
                  id="fixedCosts"
                  type="text"
                  placeholder="1.000,00"
                  value={fixedCosts}
                  onChange={(e) => setFixedCosts(sanitizeBrlValue(e))}
                />
              </div>
            </div>

            <div className="grid gap-2">
              <Label htmlFor="monthlySalary">Salário Mensal</Label>
              <div className="relative">
                <Input
                  id="monthlySalary"
                  type="text"
                  placeholder="5.000,00"
                  value={monthlySalary}
                  onChange={(e) => setMonthlySalary(sanitizeBrlValue(e))}
                />
              </div>
            </div>

            <div className="grid gap-2">
              <Label htmlFor="savingsPercentage">
                Quanto você guarda por mês?
              </Label>
              <div className="flex items-center gap-4">
                <Slider
                  id="savingsPercentage"
                  min={1}
                  max={100}
                  step={1}
                  value={[savingsPercentage]}
                  onValueChange={(value) => setSavingsPercentage(value[0])}
                />
                <span className="font-bold">{savingsPercentage}%</span>
              </div>
            </div>

            <div className="flex justify-between">
              <Button
                type="submit"
                variant={"default"}
                className="sm:w-[150px]"
                disabled={!isVerified}
              >
                Calcular
              </Button>
              <Button onClick={clear} variant={"ghost"}>
                Limpar
              </Button>
            </div>

            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
          </form>

          {results && <EmergencyFundResponse results={results} />}
        </CardContent>
      </Card>
    </div>
  );
};
